import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { OrderStateService } from '../../../order/services/order-state.service';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { GiftCard } from '../../../giftcard/models/giftcard';
import { DiscountCoupon } from '../../models/discount-coupon';
import { DiscountStateService } from '../../services/discount-state.service';
import { PaymentGiftStateService } from '../../services/payment-gift-state.service';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { InactiveTimerService } from '../../../../services/inactive-timer.service';
import { BookingOrderService } from '../../../booking/services/booking-order.service';
import { NotificationService } from '../../../general/services/notification.service';
import { OrderLineType } from '../../../order/models/order-line-type';
import { PaymentStateService } from '../../services/payment-state.service';

@Component({
  selector: 'app-payment-discount',
  templateUrl: './payment-discount.component.html',
  styleUrls: ['./payment-discount.component.css']
})
export class PaymentDiscountComponent implements OnInit, OnDestroy {

  faExclamationTriangle = faExclamationTriangle;

  @Input()
  showGiftCardRedeem = true;

  constructor(
    private orderStateService: OrderStateService,
    private discountStateService: DiscountStateService,
    private paymentGiftStateService: PaymentGiftStateService,
    private loadingSpinnerService: LoadingSpinnerService,
    private inactiveTimerService: InactiveTimerService,
    private bookingOrderService: BookingOrderService,
    private notificationService: NotificationService,
    private paymentStateService: PaymentStateService,
  ) { }

  cardNumber: string;
  error: string;

  ngOnInit() {
    this.paymentStateService.partnerCodeCardNumber = '';
    if (this.error) {
      this.error = '';
    }
  }

  redeem(cardNumber: string) {
    const invalidCardMessage = 'Invalid gift card code';
    this.error = '';
    this.notificationService.hide(PaymentDiscountComponent.name);

    if (!cardNumber) {
      this.notificationService.show(PaymentDiscountComponent.name, invalidCardMessage);
      return;
    }

    this.inactiveTimerService.resetTimer();

    this.loadingSpinnerService.show();

    const order = this.orderStateService.order;
    const orderLines = order.orderLines;
    const selectedBookingDate = order.bookingDate;
    const selectedBookingSlot = this.bookingOrderService.selectedBookingSlots && this.bookingOrderService.selectedBookingSlots.length ? this.bookingOrderService.selectedBookingSlots[0] : null;
    let bookingFromTime = null;
    let bookingToTime = null;
    if (selectedBookingSlot) {
      bookingFromTime = selectedBookingSlot.fromTime;
      bookingToTime = bookingFromTime + environment.globalSettings.minutesToFloat(this.bookingOrderService.maximalDurationTimeOfProduct);
    }

    const isHaveGiftCard = orderLines.some(line => line.orderLineType === OrderLineType.GiftCard);

    let bookingDate = '';
    if (selectedBookingDate) {
      bookingDate = environment.globalSettings.getIsoDateStr(selectedBookingDate.getFullYear(), selectedBookingDate.getMonth() + 1, selectedBookingDate.getDate());
    }

    this.discountStateService.addDiscountCoupon(
      cardNumber,
      orderLines.map(line => line.productId),
      orderLines.map(line => line.amount),
      orderLines.map(line => line.quantity),
      bookingDate,
      bookingFromTime,
      bookingToTime,
      order,
    ).subscribe(
      (result: boolean) => {
        if (!result) {
          this.loadingSpinnerService.hide();
          return;
        }
        this.setDiscountCouponsMessageError();
        this.recalculateGiftCardsAmount().subscribe(
          result2 => {
            const isDiscountCode = this.discountStateService.isDiscountCouponExist(cardNumber);
            if (isDiscountCode) {
              this.loadingSpinnerService.hide();
              return;
            }

            if (this.amountToPay <= 0) {
              this.loadingSpinnerService.hide();
              this.notificationService.show(PaymentDiscountComponent.name, invalidCardMessage);
              return;
            }

            if (isHaveGiftCard) {
              this.loadingSpinnerService.hide();
              this.notificationService.show(PaymentDiscountComponent.name, invalidCardMessage);
              return;
            }

            this.paymentGiftStateService.addGiftCardForRedeem(
              this.orderStateService.order.id,
              cardNumber,
              this.amountToPay,
            ).subscribe(
              isCardAccepted => {
                this.cardNumber = '';
                if (!isCardAccepted && !this.error) {
                  this.notificationService.show(PaymentDiscountComponent.name, invalidCardMessage);
                }
                this.loadingSpinnerService.hide();
              },
              (error => {
                this.notificationService.show(PaymentDiscountComponent.name, error.message);
                this.loadingSpinnerService.hide();
              })
            );
          },
          (error2) => {
            this.notificationService.show(PaymentDiscountComponent.name, error2.message);
            this.loadingSpinnerService.hide();
          }
        );
      },
      (error => {
        this.notificationService.show(PaymentDiscountComponent.name, error.message);
      })
    );
  }

  get redeemedGiftCards(): GiftCard[] {
    return this.paymentGiftStateService.redeemedGiftCards;
  }

  get discountCoupons(): DiscountCoupon[] {
    return this.discountStateService.discountCoupons;
  }

  toMoneyStr(x: number): string {
    return environment.globalSettings.toMoneyStr(x);
  }

  get isRedeemAvailable(): boolean {
    if (this.amountToPay <= 0) {
      return false;
    }
    return true;
  }

  get amountToPay(): number {
    return this.paymentStateService.amountToPay;
  }

  get amountToPayWithoutGiftCards(): number {
    return this.paymentStateService.amountToPayWithoutGiftCards;
  }

  deleteRedeemedGiftCard(redeemedGiftCard: GiftCard) {
    this.loadingSpinnerService.show();
    if (this.paymentStateService.partnerCodeCardNumber && this.paymentStateService.partnerCodeCardNumber === redeemedGiftCard.cardNumber) {
      this.paymentStateService.partnerCodeCardNumber = '';
    }
    this.paymentGiftStateService.deleteRedeemedGiftCard(redeemedGiftCard);
    this.recalculateGiftCardsAmount().subscribe(
      result => {
        this.loadingSpinnerService.hide();
      }
    );
  }

  deleteDiscountCoupon(discountCoupon: DiscountCoupon) {
    this.loadingSpinnerService.show();
    this.discountStateService.deleteDiscountCoupon(discountCoupon);
    this.recalculateGiftCardsAmount().subscribe(
      result => {
        this.loadingSpinnerService.hide();
      }
    );
  }

  recalculateGiftCardsAmount(): Observable<boolean> {
    return this.paymentGiftStateService.recalculateGiftCardForRedeem(
      this.orderStateService.order.id,
      this.amountToPayWithoutGiftCards
    );
  }

  setDiscountCouponsMessageError() {
    if (this.discountStateService.message) {
      this.error = this.discountStateService.message;
    }

  }

  ngOnDestroy(): void {
    this.notificationService.hide(PaymentDiscountComponent.name);
    this.notificationService.hide(this.paymentStateService.name);
  }


  getGiftCardName(cardNumber: string): string {
    if (this.paymentStateService.partnerCodeCardNumber && this.paymentStateService.partnerCodeCardNumber === cardNumber) {
      return 'Debited from the account';
    }
    return cardNumber;
  }

  get redeemGiftMessage(): string {
    return this.paymentStateService.isPartnerCodePaymentAllowed
      ? 'RFID personal card is used:' : 'Coupons are used:';
  }
}
